import { Clock, CheckCircle2, Send, Wallet, AlertCircle } from "lucide-react";

const cents = (c) => `$${(Number(c || 0) / 100).toFixed(2)}`;

const BUCKETS = [
  {
    key: "pending_cents",
    label: "Pending",
    hint: "Earned but still in clearing window",
    icon: Clock,
    tone: "text-amber-300",
    ring: "ring-amber-500/25",
  },
  {
    key: "available_cents",
    label: "Available",
    hint: "Ready to spend on coins or cash out",
    icon: CheckCircle2,
    tone: "text-emerald-300",
    ring: "ring-emerald-500/25",
  },
  {
    key: "scheduled_cents",
    label: "Scheduled",
    hint: "Reserved for the next payout batch",
    icon: Send,
    tone: "text-sky-300",
    ring: "ring-sky-500/25",
  },
  {
    key: "paid_cents",
    label: "Paid",
    hint: "Successfully transferred lifetime",
    icon: Wallet,
    tone: "text-indigo-300",
    ring: "ring-indigo-500/25",
  },
];

export default function BalanceCard({ balances, minimumCashCents }) {
  const reversed = Number(balances?.reversed_cents || 0);
  const belowMin =
    minimumCashCents != null &&
    Number(balances?.available_cents || 0) < Number(minimumCashCents);

  return (
    <section
      data-testid="affiliate-balance-card"
      className="rounded-xl border border-white/[0.06] bg-[#111113] p-5"
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-white/60 uppercase tracking-wider">
          Affiliate Balance
        </h2>
        <span className="text-[10px] font-mono text-white/30">USD</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {BUCKETS.map(({ key, label, hint, icon: Icon, tone, ring }) => (
          <div
            key={key}
            data-testid={`balance-${key.replace("_cents", "")}`}
            className={`rounded-lg bg-[#0a0a0a] ring-1 ${ring} p-3.5`}
          >
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/50 mb-1.5">
              <Icon className="w-3 h-3" />
              {label}
            </div>
            <div className={`text-xl font-mono font-semibold ${tone}`}>
              {cents(balances?.[key])}
            </div>
            <div className="text-[10px] text-white/30 mt-1 leading-snug">{hint}</div>
          </div>
        ))}
      </div>

      {reversed !== 0 && (
        <div
          data-testid="balance-reversed"
          className="mt-3 flex items-center justify-between text-xs px-3 py-1.5 rounded-md border border-red-500/20 bg-red-500/[0.06]"
        >
          <span className="text-white/50">Reversed lifetime (refunds &amp; chargebacks)</span>
          <span className="font-mono text-red-300">{cents(reversed)}</span>
        </div>
      )}

      {belowMin && (
        <div
          data-testid="balance-below-min"
          className="mt-3 flex items-start gap-2 text-xs text-white/60 px-3 py-2 rounded-md border border-amber-500/20 bg-amber-500/[0.06]"
        >
          <AlertCircle className="w-3.5 h-3.5 text-amber-300 mt-0.5 shrink-0" />
          <span>
            Minimum cash payout is {cents(minimumCashCents)}. You&apos;ll roll into the next batch
            once your Available balance clears the threshold — or you can spend it on Coins any
            time.
          </span>
        </div>
      )}
    </section>
  );
}
